import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Users, MessageCircle, Mail, Calendar, BookOpen, Bell, Home } from 'lucide-react';
import PublicLayout from '../public/PublicLayout';
import AnnouncementsNewsFeed from './components/AnnouncementsNewsFeed';
import DiscussionForums from './components/DiscussionForums';
import PrivateMessaging from './components/PrivateMessaging';

const CommunityDashboard = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  
  // Determine active tab based on URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/profiles')) return 'profiles';
    if (path.includes('/forums')) return 'forums';
    if (path.includes('/messages')) return 'messages';
    if (path.includes('/events')) return 'events';
    if (path.includes('/resources')) return 'resources';
    if (path.includes('/announcements')) return 'announcements';
    return 'home';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTab());

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    switch(tab) {
      case 'profiles':
        navigate('/community/profiles');
        break;
      case 'forums':
        navigate('/community/forums');
        break;
      case 'messages':
        navigate('/community/messages');
        break;
      case 'events':
        navigate('/community/events');
        break;
      case 'resources':
        navigate('/community/resources');
        break;
      case 'announcements':
        navigate('/community/announcements');
        break;
      default:
        navigate('/community');
    }
  };

  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location]);

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE}/events?limit=3`);
        const data = await response.json();
        
        if (data.success && data.data && data.data.events) {
          const mappedEvents = data.data.events.map(event => ({
            title: event.name,
            date: new Date(event.start_date).toLocaleString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            }) + ' • ' + new Date(event.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' GMT',
            type: event.event_type ? event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1) : 'Event',
            color: arcaGreen
          }));
          setEvents(mappedEvents);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        // Keep empty array if fetch fails
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchEvents();
  }, []);

  const arcaGreen = '#6B8E23';
  const arcaLightGreen = '#e8f5e9';

  return (
    <PublicLayout>
      <div className="bg-light" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <div className="d-flex">
          {/* Sidebar */}
          <div className="bg-white border-end" style={{ width: '280px', minHeight: 'calc(100vh - 200px)' }}>
            <div className="p-3">
              <h5 className="text-uppercase small fw-bold text-muted mb-3 px-2">Navigation</h5>
              <nav>
              <button
                onClick={() => handleTabChange('home')}
                className={`btn w-100 text-start mb-2 d-flex align-items-center gap-2 ${activeTab === 'home' ? 'text-white' : 'btn-light'}`}
                style={activeTab === 'home' ? { backgroundColor: arcaGreen, borderColor: arcaGreen } : {}}
              >
                <Home size={18} />
                <span>Home</span>
              </button>
              <button
                onClick={() => handleTabChange('profiles')}
                className={`btn w-100 text-start mb-2 d-flex align-items-center gap-2 ${activeTab === 'profiles' ? 'text-white' : 'btn-light'}`}
                style={activeTab === 'profiles' ? { backgroundColor: arcaGreen, borderColor: arcaGreen } : {}}
              >
                <Users size={18} />
                <span>Member Profiles</span>
              </button>
              <button
                onClick={() => handleTabChange('forums')}
                className={`btn w-100 text-start mb-2 d-flex align-items-center gap-2 ${activeTab === 'forums' ? 'text-white' : 'btn-light'}`}
                style={activeTab === 'forums' ? { backgroundColor: arcaGreen, borderColor: arcaGreen } : {}}
              >
                <MessageCircle size={18} />
                <span>Discussion Forums</span>
              </button>
              <button
                onClick={() => handleTabChange('messages')}
                className={`btn w-100 text-start mb-2 d-flex align-items-center gap-2 ${activeTab === 'messages' ? 'text-white' : 'btn-light'}`}
                style={activeTab === 'messages' ? { backgroundColor: arcaGreen, borderColor: arcaGreen } : {}}
              >
                <Mail size={18} />
                <span>Private Messages</span>
              </button>
              <button
                onClick={() => handleTabChange('events')}
                className={`btn w-100 text-start mb-2 d-flex align-items-center gap-2 ${activeTab === 'events' ? 'text-white' : 'btn-light'}`}
                style={activeTab === 'events' ? { backgroundColor: arcaGreen, borderColor: arcaGreen } : {}}
              >
                <Calendar size={18} />
                <span>Events & Webinars</span>
              </button>
              <button
                onClick={() => handleTabChange('resources')}
                className={`btn w-100 text-start mb-2 d-flex align-items-center gap-2 ${activeTab === 'resources' ? 'text-white' : 'btn-light'}`}
                style={activeTab === 'resources' ? { backgroundColor: arcaGreen, borderColor: arcaGreen } : {}}
              >
                <BookOpen size={18} />
                <span>Resource Library</span>
              </button>
              <button
                onClick={() => handleTabChange('announcements')}
                className={`btn w-100 text-start mb-2 d-flex align-items-center gap-2 ${activeTab === 'announcements' ? 'text-white' : 'btn-light'}`}
                style={activeTab === 'announcements' ? { backgroundColor: arcaGreen, borderColor: arcaGreen } : {}}
              >
                <Bell size={18} />
                <span>Announcements</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-grow-1 p-4">
          <div className="container-fluid">
            {activeTab === 'home' && (
              <div>
                <div className="mb-4">
                  <h1 className="display-5 fw-bold mb-2">ARCA Community</h1>
                  <p className="lead text-muted">
                    Connect, collaborate, and grow with respiratory care professionals across Africa
                  </p>
                </div>
                
                {/* Stats Grid */}
                <div className="row g-4 mb-5">
                  <div className="col-sm-6 col-lg-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <p className="text-muted small mb-1">Total Members</p>
                            <h3 className="fw-bold mb-0">1,247</h3>
                          </div>
                          <div className="rounded-circle p-3" style={{ backgroundColor: arcaLightGreen }}>
                            <Users style={{ color: arcaGreen }} size={24} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6 col-lg-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <p className="text-muted small mb-1">Active Today</p>
                            <h3 className="fw-bold mb-0">89</h3>
                          </div>
                          <div className="rounded-circle p-3" style={{ backgroundColor: arcaLightGreen }}>
                            <Users style={{ color: arcaGreen }} size={24} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6 col-lg-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <p className="text-muted small mb-1">Forum Posts</p>
                            <h3 className="fw-bold mb-0">3,521</h3>
                          </div>
                          <div className="rounded-circle p-3" style={{ backgroundColor: arcaLightGreen }}>
                            <MessageCircle style={{ color: arcaGreen }} size={24} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6 col-lg-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <p className="text-muted small mb-1">Resources</p>
                            <h3 className="fw-bold mb-0">1,856</h3>
                          </div>
                          <div className="rounded-circle p-3" style={{ backgroundColor: arcaLightGreen }}>
                            <BookOpen style={{ color: arcaGreen }} size={24} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Access */}
                <div className="mb-5">
                  <h4 className="fw-bold mb-3">Quick Access</h4>
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-3">
                      <div 
                        className="card border-0 shadow-sm h-100 cursor-pointer"
                        onClick={() => handleTabChange('profiles')}
                        style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <div className="card-body p-4 text-center">
                          <div className="rounded-circle mx-auto mb-3" style={{ width: '60px', height: '60px', backgroundColor: arcaLightGreen, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Users style={{ color: arcaGreen }} size={28} />
                          </div>
                          <h6 className="fw-bold mb-2">Member Profiles</h6>
                          <p className="small text-muted mb-0">Browse directory</p>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 col-lg-3">
                      <div 
                        className="card border-0 shadow-sm h-100 cursor-pointer"
                        onClick={() => handleTabChange('forums')}
                        style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <div className="card-body p-4 text-center">
                          <div className="rounded-circle mx-auto mb-3" style={{ width: '60px', height: '60px', backgroundColor: arcaLightGreen, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MessageCircle style={{ color: arcaGreen }} size={28} />
                          </div>
                          <h6 className="fw-bold mb-2">Discussion Forums</h6>
                          <p className="small text-muted mb-0">Join conversations</p>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 col-lg-3">
                      <div 
                        className="card border-0 shadow-sm h-100 cursor-pointer"
                        onClick={() => handleTabChange('events')}
                        style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <div className="card-body p-4 text-center">
                          <div className="rounded-circle mx-auto mb-3" style={{ width: '60px', height: '60px', backgroundColor: arcaLightGreen, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Calendar style={{ color: arcaGreen }} size={28} />
                          </div>
                          <h6 className="fw-bold mb-2">Events & Webinars</h6>
                          <p className="small text-muted mb-0">Upcoming events</p>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 col-lg-3">
                      <div 
                        className="card border-0 shadow-sm h-100 cursor-pointer"
                        onClick={() => handleTabChange('resources')}
                        style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <div className="card-body p-4 text-center">
                          <div className="rounded-circle mx-auto mb-3" style={{ width: '60px', height: '60px', backgroundColor: arcaLightGreen, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BookOpen style={{ color: arcaGreen }} size={28} />
                          </div>
                          <h6 className="fw-bold mb-2">Resource Library</h6>
                          <p className="small text-muted mb-0">Access resources</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div>
                  <h4 className="fw-bold mb-3">Recent Activity</h4>
                  <div className="card border-0 shadow-sm">
                    <div className="list-group list-group-flush">
                      <div className="list-group-item px-4 py-3">
                        <div className="d-flex align-items-start">
                          <div className="rounded-circle me-3 flex-shrink-0" style={{ width: '48px', height: '48px', backgroundColor: arcaLightGreen, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Users style={{ color: arcaGreen }} size={20} />
                          </div>
                          <div className="flex-grow-1">
                            <p className="mb-1"><strong>Dr. Amara Okafor</strong> joined the community</p>
                            <p className="small text-muted mb-0">2 hours ago</p>
                          </div>
                        </div>
                      </div>

                      <div className="list-group-item px-4 py-3">
                        <div className="d-flex align-items-start">
                          <div className="rounded-circle me-3 flex-shrink-0" style={{ width: '48px', height: '48px', backgroundColor: arcaLightGreen, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MessageCircle style={{ color: arcaGreen }} size={20} />
                          </div>
                          <div className="flex-grow-1">
                            <p className="mb-1"><strong>New discussion:</strong> Best practices for ventilator weaning</p>
                            <p className="small text-muted mb-0">5 hours ago</p>
                          </div>
                        </div>
                      </div>

                      <div className="list-group-item px-4 py-3">
                        <div className="d-flex align-items-start">
                          <div className="rounded-circle me-3 flex-shrink-0" style={{ width: '48px', height: '48px', backgroundColor: arcaLightGreen, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BookOpen style={{ color: arcaGreen }} size={20} />
                          </div>
                          <div className="flex-grow-1">
                            <p className="mb-1"><strong>New resource added:</strong> COVID-19 Respiratory Management Protocol</p>
                            <p className="small text-muted mb-0">1 day ago</p>
                          </div>
                        </div>
                      </div>

                      <div className="list-group-item px-4 py-3">
                        <div className="d-flex align-items-start">
                          <div className="rounded-circle me-3 flex-shrink-0" style={{ width: '48px', height: '48px', backgroundColor: arcaLightGreen, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Calendar style={{ color: arcaGreen }} size={20} />
                          </div>
                          <div className="flex-grow-1">
                            <p className="mb-1"><strong>Upcoming webinar:</strong> Advanced Airway Management Techniques</p>
                            <p className="small text-muted mb-0">2 days ago</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'profiles' && (
              <div>
                <div className="mb-4">
                  <h1 className="display-6 fw-bold mb-2">Member Profiles</h1>
                  <p className="text-muted">Connect with respiratory care professionals across Africa</p>
                </div>
                
                <div className="row g-4">
                  {[
                    { name: 'Dr. Emily Johnson', title: 'Respiratory Therapist', location: 'South Africa', bio: 'Specialized in pediatric respiratory therapy for over 10 years.', posts: 542 },
                    { name: 'Prof. Michael Chen', title: 'Clinical Director', location: 'Kenya', bio: 'Research focus on mechanical ventilation and critical care.', posts: 327 },
                    { name: 'Dr. Amina Hassan', title: 'Respiratory Consultant', location: 'Nigeria', bio: 'Advocating for respiratory therapy recognition in Africa.', posts: 421 }
                  ].map((profile, idx) => (
                    <div key={idx} className="col-md-6 col-lg-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-header text-white py-4" style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}>
                          <div className="d-flex align-items-center">
                            <div className="rounded-circle bg-white d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '64px', height: '64px' }}>
                              <Users style={{ color: arcaGreen }} size={32} />
                            </div>
                            <div className="ms-3">
                              <h5 className="mb-1 fw-bold">{profile.name}</h5>
                              <p className="mb-0 small">{profile.title}</p>
                            </div>
                          </div>
                        </div>
                        <div className="card-body">
                          <p className="text-muted mb-3">{profile.bio}</p>
                          <div className="d-flex justify-content-between align-items-center small">
                            <span className="text-muted">
                              <i className="fas fa-map-marker-alt me-1"></i>{profile.location}
                            </span>
                            <span className="text-muted">{profile.posts} posts</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'forums' && (
              <div>
                <div className="mb-4">
                  <h1 className="display-6 fw-bold mb-2">Discussion Forums</h1>
                  <p className="text-muted">Join discussions and share knowledge with peers</p>
                </div>
                <DiscussionForums user={user} />
              </div>
            )}

            {activeTab === 'messages' && (
              <div>
                <div className="mb-4">
                  <h1 className="display-6 fw-bold mb-2">Private Messages</h1>
                  <p className="text-muted">Direct communication with community members</p>
                </div>
                <PrivateMessaging user={user} />
              </div>
            )}

            {activeTab === 'events' && (
              <div>
                <div className="mb-4">
                  <h1 className="display-6 fw-bold mb-2">Events & Webinars</h1>
                  <p className="text-muted">Upcoming ARCA community events and webinars</p>
                </div>

                <div className="row g-3">
                  {loadingEvents ? (
                    <div className="col-12 text-center py-4">
                      <div className="spinner-border text-success" role="status">
                        <span className="visually-hidden">Loading events...</span>
                      </div>
                    </div>
                  ) : events.length > 0 ? (
                    events.map((event, idx) => (
                      <div key={idx} className="col-12">
                        <div className="card border-0 shadow-sm">
                          <div className="card-body p-4 border-start border-4" style={{ borderColor: `${event.color} !important` }}>
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <h5 className="fw-bold mb-2">{event.title}</h5>
                                <p className="text-muted mb-2">
                                  <Calendar size={16} className="me-2" />
                                  {event.date}
                                </p>
                                <span className="badge text-white" style={{ backgroundColor: event.color }}>{event.type}</span>
                              </div>
                              <Link to="/events" className="btn btn-sm btn-outline-secondary">View Details</Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-12 text-center py-4">
                      <p className="text-muted mb-0">No upcoming events at this time.</p>
                    </div>
                  )}
                </div>

                <div className="text-center mt-4">
                  <Link to="/events" className="btn text-white px-5" style={{ backgroundColor: arcaGreen }}>
                    View All Events
                  </Link>
                </div>
              </div>
            )}

            {activeTab === 'resources' && (
              <div>
                <div className="mb-4">
                  <h1 className="display-6 fw-bold mb-2">Resource Library</h1>
                  <p className="text-muted">Access educational materials and professional resources</p>
                </div>

                <div className="row g-4">
                  {[
                    { title: 'Clinical Guidelines', description: 'Evidence-based protocols', link: '/resources/clinical-guidelines', icon: '📄' },
                    { title: 'Training Materials', description: 'Professional development', link: '/resources/training-materials', icon: '📚' },
                    { title: 'Publications', description: 'Research and reports', link: '/resources/publications', icon: '📖' },
                    { title: 'FAQs', description: 'Common questions', link: '/resources/faqs', icon: '❓' }
                  ].map((resource, idx) => (
                    <div key={idx} className="col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <div className="d-flex align-items-start">
                            <div className="rounded me-3 flex-shrink-0" style={{ width: '56px', height: '56px', backgroundColor: arcaLightGreen, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>
                              {resource.icon}
                            </div>
                            <div>
                              <h5 className="fw-bold mb-2">{resource.title}</h5>
                              <p className="text-muted small mb-3">{resource.description}</p>
                              <Link to={resource.link} className="text-decoration-none fw-semibold" style={{ color: arcaGreen }}>
                                Browse →
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'announcements' && (
              <div>
                <div className="mb-4">
                  <h1 className="display-6 fw-bold mb-2">Announcements</h1>
                  <p className="text-muted">Latest news and updates from ARCA</p>
                </div>

                <AnnouncementsNewsFeed />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </PublicLayout>
  );
};

export default CommunityDashboard;
